import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadSimpleIcon } from '@phosphor-icons/react/dist/csr/DownloadSimple';

import { Button } from '../../design-system/primitives/Button/Button';
import { ApiError } from '../../lib/api/client';
import { saveExport, type ExportModule } from '../../lib/api/exports';
import styles from './ExportButton.module.css';

/**
 * The CSV export control, shared by the six list screens.
 *
 * ## It exports what is on screen
 *
 * `filters` is the screen's OWN outgoing query — the object its `toQuery()`
 * already hands to its list fetch, minus the cursor. The file therefore
 * matches the rows the user is looking at, and this component never
 * re-derives a filter set of its own that could drift from the list's.
 *
 * The cursor and `per_page` are deliberately absent: the export routes reject
 * both (422). An export is the whole filtered set, not a page of it.
 *
 * ## There is no export permission
 *
 * `canExport` is the module's own `canRead*` result, evaluated by the screen
 * (Global Constraint 7: no new permission exists for exporting). It is passed
 * in rather than looked up here so this component holds no module ->
 * permission mapping — a second copy of that mapping would be one more thing
 * to drift from the server's gate, which is the real one.
 *
 * ## The 422 paints the SERVER's sentence, not ours
 *
 * Everywhere else in this codebase (`LoginPage`, `ChallengeForm`) the
 * envelope's `message` is deliberately thrown away, because those messages are
 * a fixed English string per code that the specification marks "not
 * localised". The export refusal is the exception, and for two reasons:
 * `CsvExport::guard()` builds it with `__('errors.export_too_large', [...])`,
 * so it arrives in the locale `downloadFile()` asked for; and it interpolates
 * `:count` and `:cap`, so it tells the supervisor HOW far over the cap they
 * are, which is the number they need to narrow the filters by. A fixed local
 * string would discard both, and would also mislabel any OTHER 422 from these
 * routes (a `cursor` or `per_page` the export rejects) as "too large".
 *
 * `t('exports.tooLarge')` remains as the fallback for an envelope that carried
 * no usable prose.
 *
 * ## The button always comes back
 *
 * `setBusy(false)` is in a `finally`. A button left non-actionable by a
 * failed export strands the user with no way to retry and no way to reach the
 * message telling them to narrow the filters.
 *
 * ## Busy is `loading`, not `disabled`
 *
 * `Button`'s `loading` sets `aria-busy`/`aria-disabled` and swallows the
 * click, WITHOUT leaving the tab order — which `disabled` would do. The user
 * has just clicked this control, so it is holding focus at the exact moment
 * it goes busy; taking it out of the accessibility tree there announces the
 * busy state to nobody. Every other in-flight button in this codebase uses
 * `loading`, and the guard inside `Button` is code, not CSS.
 */
/**
 * The envelope's own prose, when there is any worth painting.
 *
 * `errorFromResponse` substitutes a fixed English `'The request failed.'` when
 * the body carried no message at all — a truncated body, a proxy's HTML. That
 * string is not a sentence for an Arabic-reading supervisor, so it is treated
 * as "no message" and the translated copy stands in.
 */
function serverMessage(error: ApiError): string | null {
  const message = error.message.trim();
  if (message === '' || message === 'The request failed.') return null;
  return message;
}

interface ExportButtonProps {
  module: ExportModule;
  /** The filters currently on screen — the file matches what the user sees. */
  filters: Record<string, string | number | boolean | undefined>;
  /** The module's own `canRead*` helper, already evaluated by the screen. */
  canExport: boolean;
}

export function ExportButton({ module, filters, canExport }: ExportButtonProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setBusy(true);
    setError(null);

    try {
      await saveExport(module, filters);
    } catch (cause) {
      // Branch on `status`, never on `message`: the status is the contract.
      // The message is then RENDERED, which is the one place in this codebase
      // where the envelope's prose reaches the screen — see the block comment.
      setError(
        cause instanceof ApiError && cause.status === 422
          ? serverMessage(cause) ?? t('exports.tooLarge')
          : t('exports.failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  if (!canExport) {
    return null;
  }

  return (
    <div className={styles['wrapper']}>
      <Button
        variant="secondary"
        loading={busy}
        icon={<DownloadSimpleIcon size={16} />}
        onClick={() => {
          void handleClick();
        }}
      >
        {busy ? t('exports.inProgress') : t('exports.button')}
      </Button>
      {error !== null ? (
        <p className={styles['error']} role="alert" aria-atomic="true">
          {error}
        </p>
      ) : null}
    </div>
  );
}
